/** @jsxImportSource @emotion/react */

import { useContext, useEffect } from 'react';
import { useLocation } from 'react-router-dom';

import { DashboardContext } from 'context/Dashboards';
import { PluginContext } from 'context/Plugin';
import { PersistentStateContext } from 'context/PersistentState';
import { RedirectContext } from 'context/Redirect';
import { CustomProductRedirects, defaultSignupProduct } from 'pages/SignUp/CustomSignup.data';
import { signUpVariant } from 'pages/SignUp/customSignupHelpers';
import { setSSItem } from 'tracking';
import { useHistory } from 'utils/router';

import { CreateUserCustomInfo } from './CreateUserCustomInfo';
import { CreateUserSSO } from './CreateUserSSO';

const defaultContent = defaultSignupProduct({ key: '', redirect: '' });

interface CreateUserPageProps {
  incomingEmail?: string;
  title: string;
}

export const CreateUserPage = ({ incomingEmail, title }: CreateUserPageProps) => {
  const { search } = useLocation();
  const history = useHistory();
  const { dashboard } = useContext(DashboardContext);
  const { plugin } = useContext(PluginContext);
  const [, { setPersistentData }] = useContext(PersistentStateContext);
  const [{ customContent }, { updateCustomContent }] = useContext(RedirectContext);

  useEffect(() => {
    document.title = `Grafana Cloud - ${title}`;

    return () => {
      document.title = 'Grafana Cloud';
    };
  }, [title]);

  useEffect(() => {
    const customSignupProduct = signUpVariant(search, { dashboard, plugin });

    if (customSignupProduct) {
      const params = new URLSearchParams(search);
      params.set('redirectPath', customSignupProduct.key as CustomProductRedirects);
      history.replace({ search: params.toString() });

      // Some variants (e.g. marketplace) record a dedicated signupForm, picked up by the email and
      // SSO sign-up paths via getSSItem('signupForm').
      if (customSignupProduct.signupForm) {
        setSSItem('signupForm', customSignupProduct.signupForm);
      }
    }

    setPersistentData(customSignupProduct);
    updateCustomContent(customSignupProduct);
  }, [search, dashboard, plugin, setPersistentData, history, updateCustomContent]);

  return (
    <>
      <CreateUserCustomInfo customContent={customContent ?? defaultContent} />
      <CreateUserSSO incomingEmail={incomingEmail} />
    </>
  );
};
